import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Brain, Target, Trophy, ArrowRight, Star, Zap, BookOpen, Compass,
  Eye, Lightbulb, Heart, Gauge, Rocket, Users2, CalendarCheck, Award, Flag,
} from "lucide-react";
import Logo from "@/components/Logo";
import DemoBookingSection from "@/components/DemoBookingSection";

const IMG_HERO = "https://static.prod-images.emergentagent.com/jobs/acd67a6d-fe63-471f-993b-7d6f0f39056a/images/c1b3f2bcb71c9451fd8ce20e70b68a0c665ff6432ddd6d690430d017cfabe5ab.jpeg";
const IMG_MIND = "https://static.prod-images.emergentagent.com/jobs/acd67a6d-fe63-471f-993b-7d6f0f39056a/images/a2356ae5aafda8928866cf1c7dcf4b8070b42613ebcdb2791a18335c5e964e28.jpeg";
const IMG_KIDS = "https://static.prod-images.emergentagent.com/jobs/acd67a6d-fe63-471f-993b-7d6f0f39056a/images/ab8a67729b323a0221c234a7d495be29e7d00075fe781d1056e6d6ec218e79b1.jpeg";

const benefits = [
  { icon: Brain, title: "Stronger Mental Skills", body: "Abacus practice engages visualisation, memory, concentration and mental processing. Children learn to hold information in their minds and work with it.", card: "bg-sky-light", iconCls: "bg-sky text-white" },
  { icon: Target, title: "Better Focus & Concentration", body: "Abacus practice requires attention and consistency. Children learn to stay focused on a task, manage distractions and keep working until they reach a solution.", card: "bg-lemon/50", iconCls: "bg-lemon text-ink" },
  { icon: Lightbulb, title: "Logical Thinking", body: "Not simply memorising answers — children learn patterns, sequences and methods that encourage a structured, logical approach to problems.", card: "bg-watermelon-light", iconCls: "bg-watermelon text-white" },
  { icon: Compass, title: "Problem-Solving Skills", body: "Children learn to break problems down, understand them and find solutions — habits useful in mathematics, academics and everyday life.", card: "bg-leaf/15", iconCls: "bg-leaf text-white" },
  { icon: Trophy, title: "Confidence Through Achievement", body: "Every problem solved is a small achievement. As challenges are overcome, one important belief takes root: “I can figure this out.”", card: "bg-sky-light", iconCls: "bg-sky text-white" },
  { icon: Gauge, title: "Speed with Accuracy", body: "Regular practice makes children quicker at processing numbers while staying accurate — and far more confident under pressure.", card: "bg-lemon/50", iconCls: "bg-lemon text-ink" },
];

const skills = [
  { label: "Focus", body: "Helps children stay engaged with what they are doing.", cls: "bg-sky text-white" },
  { label: "Visualisation", body: "Helps them create and work with mental images.", cls: "bg-watermelon text-white" },
  { label: "Memory", body: "Helps them retain and use information.", cls: "bg-lemon text-ink" },
  { label: "Logical thinking", body: "Helps them understand patterns and relationships.", cls: "bg-leaf text-white" },
  { label: "Problem-solving", body: "Helps them approach challenges with a clear mind.", cls: "bg-watermelon text-white" },
  { label: "Confidence", body: "Gives them the courage to take on something difficult.", cls: "bg-lemon text-ink" },
  { label: "Discipline", body: "Teaches the value of regular practice and persistence.", cls: "bg-sky text-white" },
];

const journey = [
  { n: "1", title: "Learn", body: "Children begin by understanding numbers and basic concepts using the physical abacus.", color: "bg-sky text-white" },
  { n: "2", title: "Practise", body: "Regular practice builds familiarity, accuracy, concentration and confidence.", color: "bg-lemon text-ink" },
  { n: "3", title: "Visualise", body: "Gradually, children begin to imagine the abacus and beads in their minds.", color: "bg-watermelon text-white" },
  { n: "4", title: "Think", body: "They learn to process numbers mentally instead of depending on the physical abacus.", color: "bg-leaf text-white" },
  { n: "5", title: "Solve", body: "They grow comfortable solving increasingly challenging problems independently.", color: "bg-sky text-white" },
  { n: "6", title: "Believe", body: "Every new achievement builds confidence in their own ability.", color: "bg-watermelon text-white" },
];

const beyond = [
  { icon: BookOpen, title: "School & Academics", body: "Concentration, memory and logical thinking support children across subjects and academic challenges.", iconCls: "bg-sky text-white" },
  { icon: Award, title: "Competitive Exams", body: "Speed, accuracy, concentration and problem-solving are invaluable when children face time-bound tests.", iconCls: "bg-lemon text-ink" },
  { icon: Compass, title: "Everyday Life", body: "Children constantly encounter situations that require them to think, decide, solve and adapt.", iconCls: "bg-watermelon text-white" },
  { icon: Rocket, title: "The Future", body: "The world doesn't just need people who can remember information — it needs people who can think, learn, adapt and solve problems.", iconCls: "bg-leaf text-white" },
];

const whyUs = [
  { icon: Flag, title: "Structured Learning", body: "A progressive journey that lets children build their skills step by step.", iconCls: "bg-sky text-white" },
  { icon: Zap, title: "Engaging Practice", body: "Interactive exercises and challenges that make regular practice genuinely enjoyable.", iconCls: "bg-watermelon text-white" },
  { icon: CalendarCheck, title: "Track Progress", body: "Children see their improvement, celebrate milestones and know how far they've come.", iconCls: "bg-lemon text-ink" },
  { icon: Trophy, title: "Build Confidence", body: "Every completed challenge and achievement gives children another reason to believe in themselves.", iconCls: "bg-leaf text-white" },
  { icon: Rocket, title: "Learn Through Practice", body: "Consistent practice turns small improvements into lasting skills.", iconCls: "bg-sky text-white" },
];

const wordCluster = ["Calculate.", "Concentrate.", "Visualise.", "Think.", "Solve.", "Believe."];
const ticker = ["FOCUS", "CONFIDENCE", "VISUALISATION", "MEMORY", "LOGIC", "PROBLEM-SOLVING", "DISCIPLINE", "SPEED", "ACCURACY", "BELIEF"];
const beadColors = ["bg-watermelon", "bg-sky", "bg-lemon", "bg-leaf"];

const learnVerbs = [
  { word: "focus", cls: "text-watermelon" },
  { word: "visualise", cls: "text-sky" },
  { word: "remember", cls: "text-leaf" },
  { word: "think logically", cls: "text-watermelon" },
  { word: "solve problems", cls: "text-sky" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-canvas text-ink overflow-x-hidden">
      <style>{`
        @keyframes mwFloat { 0%,100% { transform: translateY(0) rotate(var(--r,0deg)); } 50% { transform: translateY(-14px) rotate(var(--r,0deg)); } }
        .mw-float { animation: mwFloat 4s ease-in-out infinite; }
        .mw-float-slow { animation: mwFloat 6s ease-in-out infinite; }
        @keyframes mwSpin { to { transform: rotate(360deg); } }
        .mw-spin-slow { animation: mwSpin 14s linear infinite; }
        @keyframes mwTicker { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .mw-ticker { animation: mwTicker 28s linear infinite; }
      `}</style>

      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-cloud">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="about-logo-link"><Logo /></Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="about-signin-link" className="hidden sm:inline-flex items-center px-4 py-2 rounded-full bg-white border-2 border-cloud text-ink text-sm font-bold hover:border-sky">
              Sign in
            </Link>
            <Link to="/signup" data-testid="about-signup-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-watermelon text-white text-sm font-bold btn-tactile hover:bg-watermelon-hover">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden">
        {/* colourful blob backdrop */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-lemon/40 blur-3xl pointer-events-none" />
        <div className="absolute top-20 -right-24 w-[28rem] h-[28rem] rounded-full bg-sky/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-1/3 w-80 h-80 rounded-full bg-watermelon/15 blur-3xl pointer-events-none" />
        {/* floating beads */}
        <span className="mw-float absolute top-24 left-[8%] w-6 h-6 rounded-full bg-watermelon hidden md:block" style={{ "--r": "0deg" }} />
        <span className="mw-float-slow absolute top-40 right-[10%] w-8 h-8 rounded-full bg-sky hidden md:block" />
        <span className="mw-float absolute bottom-24 left-[15%] w-5 h-5 rounded-full bg-lemon hidden md:block" style={{ animationDelay: "1s" }} />
        <span className="mw-float-slow absolute bottom-40 right-[18%] w-5 h-5 rounded-full bg-leaf hidden md:block" style={{ animationDelay: "0.6s" }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 sm:pt-16 sm:pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm border border-cloud text-sm font-bold text-ink mb-6">
              <Sparkles className="w-4 h-4 text-watermelon" /> More than numbers. More than calculations.
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Build a <span className="text-sky">Mind</span><br className="hidden sm:block" /> That Can <span className="text-watermelon">Think.</span>
            </h1>
            <p className="max-w-xl mx-auto lg:mx-0 text-lg sm:text-xl text-stone mb-6">
              At MathWhiz Beads, we use the abacus to help children develop <strong className="text-ink">focus, concentration, visualisation, logical thinking, problem-solving and confidence</strong> — skills that extend far beyond mathematics.
            </p>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-3 gap-y-2 mb-8 font-heading font-bold text-lg sm:text-xl">
              <span className="text-sky">Learn.</span>
              <span className="text-leaf">Think.</span>
              <span className="text-watermelon">Solve.</span>
              <span className="text-ink">Believe.</span>
            </div>
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              <Link to="/signup" data-testid="hero-cta-signup" className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover">
                Get Started <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="#book-demo" data-testid="hero-cta-demo" className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-lemon text-ink font-heading font-bold text-lg btn-tactile hover:brightness-95">
                <CalendarCheck className="w-5 h-5" /> Book a Free Demo Class
              </a>
              <Link to="/login" data-testid="hero-cta-login" className="inline-flex items-center h-14 px-7 rounded-full bg-white border-2 border-cloud text-ink font-heading font-bold text-lg hover:border-sky">
                I already have an account
              </Link>
            </div>
          </div>

          {/* Hero image */}
          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="mw-float-slow relative rounded-[2.5rem] overflow-hidden card-tactile rotate-2">
              <img src={IMG_HERO} alt="Colourful abacus with floating beads" className="w-full h-auto block" data-testid="about-hero-image" />
            </div>
            {/* rotating star badge */}
            <div className="absolute -top-5 -left-3 sm:-left-6 w-24 h-24 sm:w-28 sm:h-28">
              <div className="mw-spin-slow absolute inset-0">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs><path id="mwCircle" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" /></defs>
                  <text className="fill-ink font-bold" style={{ fontSize: "11.5px", letterSpacing: "1.5px" }}>
                    <textPath href="#mwCircle">LEARN • THINK • SOLVE • BELIEVE • </textPath>
                  </text>
                </svg>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="w-10 h-10 rounded-full bg-lemon flex items-center justify-center shadow-lg">
                  <Star className="w-5 h-5 text-ink" fill="currentColor" />
                </span>
              </div>
            </div>
            {/* mini beads */}
            <div className="absolute -bottom-4 -right-2 sm:-right-5 flex gap-1.5 p-2.5 bg-white rounded-2xl shadow-lg rotate-3">
              {beadColors.map((c, i) => <span key={i} className={`w-4 h-4 rounded-full ${c}`} />)}
            </div>
          </div>
        </div>
      </section>

      {/* ── Colourful ticker ── */}
      <div className="bg-watermelon py-3 overflow-hidden -rotate-1 my-2" aria-hidden="true">
        <div className="mw-ticker flex whitespace-nowrap gap-6 w-max">
          {[...ticker, ...ticker].map((w, i) => (
            <span key={i} className="inline-flex items-center gap-6 font-heading font-bold text-white text-lg tracking-wider">
              {w} <Star className="w-4 h-4 text-lemon" fill="currentColor" />
            </span>
          ))}
        </div>
      </div>

      {/* ── Book a free demo class (video + lead form) ── */}
      <DemoBookingSection />

      {/* ── Inspirational band ── */}
      <section className="bg-ink text-white relative overflow-hidden">
        <span className="absolute top-6 left-8 w-4 h-4 rounded-full bg-lemon" />
        <span className="absolute bottom-8 right-10 w-5 h-5 rounded-full bg-watermelon" />
        <span className="absolute top-1/2 right-1/4 w-3 h-3 rounded-full bg-sky" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-center relative">
          <p className="text-xl sm:text-2xl font-heading font-bold leading-snug">
            In a world full of distractions, we help build what matters the most.
          </p>
          <p className="mt-3 text-lg sm:text-xl font-heading font-bold">
            <span className="text-sky">Focus.</span> <span className="text-lemon">Confidence.</span> <span className="text-watermelon">The ability to think.</span>
          </p>
        </div>
      </section>

      {/* ── The Abacus Is Just the Beginning ── */}
      <section className="bg-sky-light/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="rounded-[2.5rem] overflow-hidden card-tactile -rotate-1 hover:rotate-0 transition-transform">
              <img src={IMG_KIDS} alt="Children learning together around a colourful abacus" className="w-full h-auto block" data-testid="about-kids-image" />
            </div>
            <div className="absolute -top-4 -right-3 rotate-6 px-4 py-2 rounded-full bg-lemon text-ink text-sm font-bold shadow-lg">
              Small beads. Big possibilities.
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-ink mb-6 leading-tight">
              The Abacus Is Just the <span className="text-watermelon">Beginning.</span>
            </h2>
            <p className="text-lg text-stone mb-5">
              An abacus may look like a simple tool for learning numbers. But when a child learns to use it, something much bigger happens.
            </p>
            <p className="text-lg font-semibold text-ink mb-2">
              They learn to{" "}
              {learnVerbs.map((v, i) => (
                <span key={v.word}>
                  <span className={v.cls}>{v.word}</span>
                  {i < learnVerbs.length - 1 ? ", " : "."}
                </span>
              ))}
            </p>
            <p className="text-lg font-semibold text-ink mb-5">
              And most importantly, they learn to <span className="text-watermelon">believe in themselves.</span>
            </p>
            <p className="text-lg text-stone">
              At MathWhiz Beads, we believe mathematics is not just about finding the right answer — <strong className="text-ink">it's about developing the mind that can find it.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ── Why Abacus ── */}
      <section className="bg-white border-y border-cloud">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-light text-sky-hover font-bold text-sm mb-4">
              <Brain className="w-4 h-4" /> Why Abacus?
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4 leading-tight">
              We're not just teaching children to calculate. <span className="text-watermelon">We're teaching them to think.</span>
            </h2>
            <p className="text-lg text-stone">
              The abacus gives children a unique way to see, understand and work with numbers. With regular practice, children gradually move from physically moving beads to visualising them in their minds.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className={`p-6 sm:p-7 rounded-3xl card-tactile hover:-translate-y-1 hover:rotate-1 transition-transform ${b.card}`}>
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 shadow-md ${b.iconCls}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold text-ink mb-2">{b.title}</h3>
                  <p className="text-sm sm:text-base text-stone leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── More Than Math ── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lemon/50 text-ink font-bold text-sm mb-4">
            <Sparkles className="w-4 h-4" /> More Than Math.
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-4 leading-tight">
            The numbers are the starting point. <span className="text-watermelon">The skills go much further.</span>
          </h2>
          <p className="text-lg text-stone">One skill strengthens another — better focus supports better learning, better understanding builds confidence, and every challenge creates another opportunity to grow.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {skills.map((s) => (
            <div key={s.label} className={`p-5 rounded-3xl card-tactile hover:-translate-y-1 hover:-rotate-1 transition-transform ${s.cls}`}>
              <div className="font-heading text-lg font-bold mb-1">{s.label}</div>
              <p className="text-sm leading-snug opacity-90">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Journey ── */}
      <section className="bg-watermelon-light/60 border-y border-cloud">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-leaf/15 text-leaf font-bold text-sm mb-4">
              <Rocket className="w-4 h-4" /> From Beads to Mental Math.
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-4 leading-tight">
              Every child's journey begins with something they can <span className="text-sky">see and touch.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {journey.map((j) => (
              <div key={j.n} className="p-6 bg-white rounded-3xl card-tactile relative hover:-translate-y-1 transition-transform">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-heading text-lg font-bold mb-4 shadow-md ${j.color}`}>{j.n}</div>
                <h3 className="font-heading text-xl font-bold text-ink mb-2">{j.title}</h3>
                <p className="text-sm text-stone leading-relaxed">{j.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 max-w-4xl mx-auto grid lg:grid-cols-2 gap-8 items-center">
            <div className="rounded-[2.5rem] overflow-hidden card-tactile rotate-1 hover:rotate-0 transition-transform">
              <img src={IMG_MIND} alt="A child visualising a glowing abacus in their mind" className="w-full h-auto block" data-testid="about-mind-image" />
            </div>
            <p className="text-xl text-stone text-center lg:text-left">
              From moving beads with their hands to moving them <strong className="text-ink">in their minds.</strong>
              <br /><span className="font-heading font-bold text-watermelon text-2xl block mt-3">That's the MathWhiz journey.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ── Beyond the classroom ── */}
      <section className="bg-sky-light/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-3xl mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-sky-hover font-bold text-sm mb-4">
              <Users2 className="w-4 h-4" /> Skills That Stay Beyond the Classroom.
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-4 leading-tight">
              The real value of learning isn't limited to one subject.
            </h2>
            <p className="text-lg text-stone">The ability to focus, think clearly, solve problems and stay confident helps children in every corner of life.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {beyond.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="p-6 bg-white rounded-3xl card-tactile hover:-translate-y-1 transition-transform">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 shadow-md ${b.iconCls}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-ink mb-1">{b.title}</h3>
                  <p className="text-sm text-stone leading-relaxed">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── They Start With Beads ── */}
      <section className="bg-ink text-white relative overflow-hidden">
        <span className="absolute top-10 right-[12%] w-5 h-5 rounded-full bg-lemon/70" />
        <span className="absolute bottom-12 left-[10%] w-4 h-4 rounded-full bg-sky/70" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center relative">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            They Start With <span className="text-lemon">Beads.</span><br />They Grow With the <span className="text-sky">Skills.</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {wordCluster.map((w, i) => (
              <span
                key={w}
                className={`px-4 py-2 rounded-full font-heading font-bold text-lg shadow-lg ${
                  i % 3 === 0 ? "bg-watermelon text-white" : i % 3 === 1 ? "bg-lemon text-ink" : "bg-sky text-white"
                }`}
              >
                {w}
              </span>
            ))}
          </div>
          <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-4">
            The goal isn't for children to depend on the abacus forever — it's to help them develop the ability to eventually do without it.
          </p>
          <p className="text-lg sm:text-xl font-heading font-bold text-lemon">
            The beads may disappear. The skills don't.
          </p>
        </div>
      </section>

      {/* ── Why MathWhiz Beads ── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-watermelon-light text-watermelon font-bold text-sm mb-4">
            <Star className="w-4 h-4" /> Why MathWhiz Beads?
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink mb-4 leading-tight">
            A modern learning experience built around <span className="text-sky">timeless skills.</span>
          </h2>
          <p className="text-lg text-stone">We combine the proven concept of abacus learning with an engaging, structured practice experience that keeps children motivated.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {whyUs.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="p-6 bg-white rounded-3xl card-tactile hover:-translate-y-1 hover:rotate-1 transition-transform">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 shadow-md ${f.iconCls}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-lg font-bold text-ink mb-1">{f.title}</h3>
                <p className="text-sm text-stone leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── For Parents ── */}
      <section className="bg-lemon/40 border-y border-cloud">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-watermelon font-bold text-sm mb-4">
              <Heart className="w-4 h-4" /> For Parents
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ink leading-tight">
              You don't need your child to be the smartest in the room.
            </h2>
          </div>
          <div className="p-6 sm:p-8 bg-white rounded-3xl card-tactile">
            <p className="text-lg text-stone mb-4">You want them to be able to <strong className="text-ink">face the room with confidence.</strong></p>
            <ul className="space-y-3 text-lg text-ink mb-6">
              <li className="flex items-start gap-2"><span className="w-2.5 h-2.5 rounded-full bg-watermelon mt-2.5 shrink-0" /> To concentrate when things get difficult.</li>
              <li className="flex items-start gap-2"><span className="w-2.5 h-2.5 rounded-full bg-sky mt-2.5 shrink-0" /> To think when there isn't an obvious answer.</li>
              <li className="flex items-start gap-2"><span className="w-2.5 h-2.5 rounded-full bg-leaf mt-2.5 shrink-0" /> To keep trying when they make mistakes.</li>
              <li className="flex items-start gap-2"><span className="w-2.5 h-2.5 rounded-full bg-lemon mt-2.5 shrink-0" /> And to believe: <strong>“I can figure this out.”</strong></li>
            </ul>
            <p className="text-lg text-stone mb-2">That's the kind of confidence we want to build.</p>
            <p className="text-lg font-heading font-bold text-ink">
              Because marks matter. <span className="text-watermelon">But the ability to learn, think and solve matters for life.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ── Vision ── */}
      <section className="bg-leaf/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-leaf/15 text-leaf font-bold text-sm mb-4">
            <Eye className="w-4 h-4" /> Our Vision
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-tight mb-6">
            To help children become <span className="text-watermelon">confident thinkers.</span>
          </h2>
          <p className="text-lg text-stone mb-4">
            We want every child to discover that learning is not about being naturally brilliant. It's about practising, improving, overcoming challenges and believing that they can get better.
          </p>
          <p className="text-lg text-stone">
            The abacus gives them a place to begin. We want to help them build skills that stay with them for life.
          </p>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 pt-8">
        <div className="relative overflow-hidden rounded-[2.5rem] card-tactile bg-watermelon text-white p-8 sm:p-12 text-center">
          <span className="absolute top-8 left-10 w-4 h-4 rounded-full bg-lemon" />
          <span className="absolute top-16 right-16 w-5 h-5 rounded-full bg-sky" />
          <span className="absolute bottom-10 left-1/4 w-3 h-3 rounded-full bg-white/60" />
          <span className="absolute bottom-16 right-1/3 w-4 h-4 rounded-full bg-leaf" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4">
              Give Your Child More Than Another Activity.
            </h2>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-4">
              Give them an opportunity to focus, think, practise, achieve and grow. Give them the confidence to face challenges and say:
            </p>
            <p className="font-heading text-2xl sm:text-3xl font-bold text-lemon mb-8">“I can do this.”</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/signup" data-testid="footer-cta-signup" className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-white text-watermelon font-heading font-bold text-lg btn-tactile hover:bg-lemon hover:text-ink">
                Start the MathWhiz Journey Today <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="#book-demo" data-testid="footer-cta-demo" className="inline-flex items-center gap-2 h-14 px-7 rounded-full bg-lemon text-ink font-heading font-bold text-lg btn-tactile hover:brightness-95">
                <CalendarCheck className="w-5 h-5" /> Book a Free Demo Class
              </a>
              <Link to="/login" data-testid="footer-cta-login" className="inline-flex items-center h-14 px-7 rounded-full bg-white/10 border-2 border-white/60 text-white font-heading font-bold text-lg hover:bg-white/20">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-cloud bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <div className="inline-block mb-3"><Logo size="sm" /></div>
          <p className="font-heading font-bold text-lg mb-1">
            <span className="text-sky">Learn.</span> <span className="text-leaf">Think.</span> <span className="text-watermelon">Solve.</span> <span className="text-ink">Believe.</span>
          </p>
          <p className="text-sm text-stone mb-5">Small beads. Big possibilities.</p>
          <div className="flex items-center justify-center gap-4 text-sm font-bold text-stone">
            <Link to="/about" className="hover:text-ink">About</Link>
            <span className="text-cloud">•</span>
            <Link to="/login" className="hover:text-ink">Sign in</Link>
            <span className="text-cloud">•</span>
            <Link to="/signup" className="hover:text-ink">Get started</Link>
          </div>
          <p className="mt-5 text-xs text-stone">© {new Date().getFullYear()} MathWhiz Beads. Made for curious kids.</p>
        </div>
      </footer>
    </div>
  );
}
